/**
 * Переиспользуемый компонент поиска с debounce
 * Дата: 30.01.2026
 * 
 * Используется в:
 * - Users.tsx — поиск пользователей по имени/email
 * - Families.tsx — поиск семей по имени/телефону  
 * - Services.tsx — поиск услуг по названию/коду
 * - Templates.tsx — поиск шаблонов по названию/содержимому
 * - Messages.tsx — поиск переписок по имени/телефону
 * - AutoReplies.tsx — поиск правил по названию/паттерну
 * - PhoneBinding.tsx — поиск пациентов
 * 
 * Связан с: components/ui/index.ts (экспорт)
 */
import { useState, useEffect, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface SearchInputProps {
  /** Текущее значение поиска */
  value: string;
  /** Callback при изменении значения (вызывается после debounce) */
  onChange: (value: string) => void;
  /** Placeholder текст */
  placeholder?: string;
  /** Задержка debounce в мс (по умолчанию 300) */
  debounceMs?: number;
  /** Дополнительные CSS классы */
  className?: string;
  /** Показывать кнопку очистки */
  showClear?: boolean;
  /** Размер: sm, md, lg */
  size?: 'sm' | 'md' | 'lg';
  /** Disabled состояние */
  disabled?: boolean;
  /** Автофокус */
  autoFocus?: boolean;
  /** data-testid для тестов */
  testId?: string;
}

/**
 * SearchInput — унифицированный компонент поиска
 * 
 * @example
 * // Базовое использование
 * <SearchInput 
 *   value={search} 
 *   onChange={setSearch} 
 *   placeholder="Поиск..." 
 * />
 * 
 * @example
 * // С кастомным debounce и кнопкой очистки
 * <SearchInput 
 *   value={search} 
 *   onChange={handleSearch} 
 *   debounceMs={500}
 *   showClear
 *   size="lg"
 * />
 */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Поиск...',
  debounceMs = 300,
  className,
  showClear = true,
  size = 'md',
  disabled = false,
  autoFocus = false,
  testId,
}: SearchInputProps) {
  // Локальное состояние для мгновенного отображения
  const [localValue, setLocalValue] = useState(value);

  // Синхронизация с внешним значением
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounced onChange
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localValue !== value) {
        onChange(localValue);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [localValue, debounceMs, onChange, value]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  }, []);

  const handleClear = useCallback(() => {
    setLocalValue('');
    onChange('');
  }, [onChange]);

  // Размеры
  const sizeClasses = {
    sm: 'h-8 text-sm pl-8 pr-8',
    md: 'h-10 text-base pl-10 pr-10',
    lg: 'h-12 text-lg pl-12 pr-12',
  };

  const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const iconLeftPosition = {
    sm: 'left-2',
    md: 'left-3',
    lg: 'left-3',
  };

  const iconRightPosition = {
    sm: 'right-2',
    md: 'right-3',
    lg: 'right-3',
  };

  return (
    <div className={cn('relative', className)}>
      {/* Иконка поиска */}
      <Search 
        className={cn(
          'absolute top-1/2 -translate-y-1/2 text-shell-muted pointer-events-none',
          iconSizeClasses[size],
          iconLeftPosition[size]
        )} 
      />
      
      {/* Input */}
      <input
        type="text"
        value={localValue}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        data-testid={testId}
        className={cn(
          'shell-input',
          sizeClasses[size]
        )}
      />
      
      {/* Кнопка очистки */}
      {showClear && localValue && !disabled && (
        <button
          type="button"
          onClick={handleClear}
          className={cn(
            'absolute top-1/2 -translate-y-1/2 text-shell-muted hover:text-shell-text',
            'transition-colors duration-150',
            iconRightPosition[size]
          )}
          aria-label="Очистить поиск"
        >
          <X className={iconSizeClasses[size]} />
        </button>
      )}
    </div>
  );
}
